import { useState } from 'react';
import { Alert, App, Card, Flex, List, Space, Tag, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { issuesConfig } from './constants/issuesConfig';

const { Text, Paragraph, Title } = Typography;

/** Alerta con solo el mensaje de error/alerta. */
const IssueAlert = ({ type, message }) => {
  if (!message) return null;

  const { icon, color } = issuesConfig[type];

  return (
    <Alert
      showIcon
      icon={
        <Text style={{ color }}>
          {icon}
        </Text>
      }
      message={
        <Paragraph
          ellipsis={{
            rows: 5,
            tooltip: {
              mouseEnterDelay: 0.3,
              styles: {
                root: { maxWidth: '40em' },
                body: {
                  maxHeight: '40vh',
                  overflow: 'auto',
                  whiteSpace: 'pre-line', // Respeta los saltos de línea en el texto
                },
              },
            },
          }}
          style={{ margin: 0, whiteSpace: 'pre-line' }}
        >
          {message}
        </Paragraph>
      }
      style={{
        textAlign: 'left',
        alignItems: 'baseline',
        backgroundColor: `${color}0d`,
        borderColor: `${color}33`,
        width: '100%',
      }}
    />
  );
};

/** 
 * Muestra el error/alerta junto al nombre del plan cuando hay un único registro.
 * Si hay varios registros, muestra un Alert que abre un modal con todos los registros.
 */
export const IssuesAlert = ({ type, items }) => {
  const [hovered, setHovered] = useState(false);
  const { modal } = App.useApp();

  if (!items?.length) return null; // Si no hay errores/alertas, no se muestra nada

  const { icon, color, label } = issuesConfig[type];
  const hasMultiple = items.length > 1;
  const issuesLabel = type === 'error'
    ? `Historial de errores (${items.length})`
    : `Historial de alertas (${items.length})`;

  const showAllIssuesModal = () => {
    modal.confirm({
      icon: null,
      title: (
        <Space>
          <Text style={{ color }}>
            {icon}
          </Text>
          {label.plural}
          <Tag color={color}>{items.length}</Tag>
        </Space>
      ),
      content: (
        <List
          split={false}
          dataSource={items}
          renderItem={({ message, plan }) => (
            <List.Item>
              <Card
                title={plan?.name ? <Text ellipsis={{ tooltip: true }}>{plan.name}</Text> : undefined}
                style={{ width: '100%' }}
              >
                <Space direction='vertical' size={4}>
                  <Text style={{ whiteSpace: 'pre-line' }}>{message}</Text>
                </Space>
              </Card>
            </List.Item>
          )}
        />
      ),
      maskClosable: true,
      cancelText: 'Cerrar',
      footer: (_, { CancelBtn }) => <CancelBtn />,
    });
  };

  if (hasMultiple) {
    return (
      <Alert
        showIcon
        icon={
          <Text style={{ color, margin: 0, paddingTop: 4 }}>
            {icon}
          </Text>
        }
        message={<Text>{issuesLabel}</Text>}
        action={<FontAwesomeIcon icon={faEye} color={color} style={{ filter: hovered ? 'brightness(1.5)' : 'none' }} />}
        style={{
          textAlign: 'left',
          alignItems: 'start',
          cursor: 'pointer',
          gap: 8,
          backgroundColor: `${color}0d`,
          borderColor: `${color}33`,
          width: '100%',
        }}
        onClick={showAllIssuesModal}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />
    );
  }

  const firstItem = items[0];

  // Usa el mismo estilo que el título de un plan exitoso para mantener consistencia visual
  return (<>
    {firstItem.plan?.name && (
      <Title level={5} ellipsis={{ rows: 2, tooltip: true }} style={{ margin: 0 }}>
        {firstItem.plan.name}
      </Title>
    )}
    <IssueAlert type={type} message={firstItem.message} />
  </>);
};
